import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { FullScreenModalViewModelKey } from '../../../../../../../../models/AppState';
import { useErrorMessages, useFullscreenModal } from '../../../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../../../models/Logging';
import { getDisplayName } from '../../../../../../../../models/NameUtils';
import { useHouseholdDeleteMutation } from '../../../../../../../../queries';
import { THEME } from '../../../../../../../components-v2/Theme/theme';
import { Button } from '../../../../../../../components/Button';
import { DeprecatedPopover, PopoverType } from '../../../../../../../components/DeprecatedPopover';
import { HomeIcon } from '../../../../../../../components/svgs/icons/HomeIcon';
import { TrashIcon } from '../../../../../../../components/svgs/icons/TrashIcon';
import { XIcon } from '../../../../../../../components/svgs/icons/XIcon';
import { TinyPopover, PopoverType as TinyPopoverType } from '../../../../../../../components/TinyPopover';
import { bs } from '../../../../../../../styles/styles';
import { styleSheet } from '../../styles';

interface Props {
	spouseContact: Api.IContact;
	onHouseholdDeleted: () => void;
	isFullScreenModal?: boolean;
	parentModalName?: string;
}

export const HouseholdKeyFactItem = ({
	spouseContact,
	onHouseholdDeleted,
	isFullScreenModal = false,
	parentModalName,
}: Props) => {
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('HouseholdKeyFactItem');
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();

	const [hoveringListItem, setHovering] = React.useState(false);
	const [showingKeyDatePopover, setShowingKeyDatePopover] = React.useState(false);
	const [showDeleteHouseholdPopover, setShowDeleteHouseholdPopover] = React.useState(false);

	const popoverTimeoutHandle = React.useRef<NodeJS.Timeout | null>(null);

	const clearPopoverTimeout = () => {
		if (popoverTimeoutHandle.current) {
			clearTimeout(popoverTimeoutHandle.current);
			popoverTimeoutHandle.current = null;
		}
	};

	const startPopoverTimeout = () => {
		clearPopoverTimeout();
		popoverTimeoutHandle.current = setTimeout(() => {
			toggleDisplayOfKeyDatePopover(false)();
			clearPopoverTimeout();
		}, 300);
	};

	const toggleDisplayOfKeyDatePopover = (_showingKeyDatePopover: boolean) => () => {
		if (showingKeyDatePopover) {
			clearPopoverTimeout();
		}
		setShowingKeyDatePopover(_showingKeyDatePopover);
		if (!_showingKeyDatePopover) {
			startPopoverTimeout();
		}
	};

	const handleShowDeleteHouseholdPopoverClose = () => {
		setShowDeleteHouseholdPopover(false);
	};

	const handleDeleteHouseholdClick = () => {
		householdDeleteMutation.mutate({ id: spouseContact.household.id });
		setShowDeleteHouseholdPopover(false);
	};

	const handleSpouseClick = () => {
		const isFullScreen = isFullScreenModal || parentModalName === FullScreenModalViewModelKey;
		if (isFullScreen) {
			fullscreenModal.history.push(`/people/${spouseContact.id}`);
			return;
		}
		history.push(`/people/${spouseContact.id}`);
	};

	const householdDeleteMutation = useHouseholdDeleteMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('DeleteHouseholdMutation', error);
		},
		onSuccess: () => {
			onHouseholdDeleted();
			toast.success('Household key fact removed');
		},
	});

	return (
		<li
			className={css(styleSheet.keyFactItem)}
			onMouseEnter={() => setHovering(true)}
			onMouseLeave={() => setHovering(false)}
		>
			<div className={css(styleSheet.keyFactItemValueContainer)}>
				<div className={css(styleSheet.keyFactItemValue, styleSheet.keyfactHouseholdItemValue)}>
					Spouse of{' '}
					<a
						href='#'
						className={css(styleSheet.householdSpouseLink)}
						onClick={ev => {
							ev.preventDefault();
							handleSpouseClick();
						}}
					>
						{getDisplayName(spouseContact)}
					</a>{' '}
					<DeprecatedPopover
						anchor={
							<span
								className={css(styleSheet.householdHomeIcon)}
								onMouseEnter={toggleDisplayOfKeyDatePopover(true)}
								onMouseLeave={startPopoverTimeout}
							>
								<HomeIcon width={12} height={14} stroke={THEME.colors.success.main} />
							</span>
						}
						dismissOnClickOutside
						isOpen={!!showingKeyDatePopover}
						onRequestClose={toggleDisplayOfKeyDatePopover(false)}
						preferredPlacement='above'
						type={PopoverType.white}
					>
						<div
							className={css(styleSheet.householdPopoverContent)}
							onMouseEnter={clearPopoverTimeout}
							onMouseLeave={startPopoverTimeout}
						>
							<strong>Household:</strong> you can customize whether to send a group email or handwritten card to
							individuals or households.
						</div>
					</DeprecatedPopover>
				</div>
			</div>

			<div
				className={`${css(styleSheet.keyFactItemActions)} keyFactItemActions ${showDeleteHouseholdPopover ? 'deleting' : ''}`}
				style={{ display: hoveringListItem || showDeleteHouseholdPopover ? 'flex' : 'none' }}
			>
				<TinyPopover
					align='end'
					anchor={
						<Button
							disabled={householdDeleteMutation.isPending}
							kind='icon'
							onClick={() => setShowDeleteHouseholdPopover(true)}
							className={css(styleSheet.keyFactItemActionButton)}
						>
							<TrashIcon />
						</Button>
					}
					arrow={{
						arrowSize: 10,
					}}
					arrowStyles={[styleSheet.deleteItemConfirmationArrow]}
					dismissOnOutsideAction={true}
					isOpen={showDeleteHouseholdPopover}
					onRequestClose={handleShowDeleteHouseholdPopoverClose}
					placement={['top']}
					type={TinyPopoverType.blue}
				>
					<div className={css(styleSheet.deleteItemConfirmationContainer)}>
						<div
							className={css(styleSheet.closeDeleteConfirmationContainer)}
							onClick={handleShowDeleteHouseholdPopoverClose}
						>
							<XIcon color={THEME.colors.text.light} height={12} width={12} />
						</div>
						<p>Do you want to delete this key fact?</p>
						<Button
							size='small'
							className={css(bs.ctaButtonDestructiveSolid)}
							onClick={handleDeleteHouseholdClick}
							disabled={householdDeleteMutation.isPending}
						>
							Delete
						</Button>
					</div>
				</TinyPopover>
			</div>
		</li>
	);
};
